/**
 * `@rails/activestorage` schedules `setTimeout(autostart, 1)` at module load and reads a bare
 * `window` inside it, so merely importing it during a Node render throws an uncatchable
 * `ReferenceError: window is not defined` and kills the process. Load it lazily, at upload time,
 * so it never reaches a server-rendered bundle.
 */
export interface DirectUploadErrorDetails {
  stage: "checksum" | "blob" | "storage"
  fileSize: number
  fileType: string
  status?: number
  storageCode?: string
  storageMessage?: string
  elapsedMs?: number
  online: boolean
}

export class DirectUploadError extends Error {
  readonly details: DirectUploadErrorDetails

  constructor(details: DirectUploadErrorDetails) {
    const failure =
      details.stage === "checksum"
        ? "file read error"
        : details.status === 0
          ? "network error"
          : `HTTP ${details.status}`
    const storageCode = details.storageCode ? ` (${details.storageCode})` : ""
    super(`Direct upload failed during ${details.stage}: ${failure}${storageCode}`)
    this.name = "DirectUploadError"
    this.details = details
  }
}

function storageErrorDetails(responseText: string): Pick<DirectUploadErrorDetails, "storageCode" | "storageMessage"> {
  if (!responseText) return {}

  const document = new DOMParser().parseFromString(responseText, "application/xml")
  const storageCode = document.querySelector("Code")?.textContent || undefined
  const storageMessage = document.querySelector("Message")?.textContent || undefined

  return { storageCode, storageMessage }
}

export async function uploadFileDirectly(
  file: File,
  apiRoot: string,
  token?: string,
  uploadPurpose?: "comment"
): Promise<string> {
  const { DirectUpload } = await import("@rails/activestorage")

  return new Promise<string>((resolve, reject) => {
    const uploadStartedAt = Date.now()
    let blobRequest: XMLHttpRequest | undefined
    let blobStartedAt: number | undefined
    let storageRequest: XMLHttpRequest | undefined
    let storageStartedAt: number | undefined

    const upload = new DirectUpload(file, `${apiRoot}/rails/active_storage/direct_uploads`, {
      directUploadWillCreateBlobWithXHR: (xhr: XMLHttpRequest) => {
        blobRequest = xhr
        blobStartedAt = Date.now()
        if (token) {
          xhr.setRequestHeader("Authorization", `Bearer ${token}`)
        }
        if (uploadPurpose) {
          xhr.setRequestHeader("X-Slant-Upload-Purpose", uploadPurpose)
        }
      },
      directUploadWillStoreFileWithXHR: (xhr: XMLHttpRequest) => {
        storageRequest = xhr
        storageStartedAt = Date.now()
      },
    })

    upload.create((error, blob) => {
      if (error) {
        const stage = storageRequest ? "storage" : blobRequest ? "blob" : "checksum"
        const request = storageRequest || blobRequest
        const startedAt = storageStartedAt || blobStartedAt || uploadStartedAt

        return reject(
          new DirectUploadError({
            stage,
            fileSize: file.size,
            fileType: file.type,
            status: request?.status,
            ...(stage === "storage" ? storageErrorDetails(request?.responseText || "") : {}),
            elapsedMs: startedAt ? Date.now() - startedAt : undefined,
            online: navigator.onLine,
          })
        )
      }
      if (!blob?.signed_id) return reject(new Error("Direct upload failed: missing signed_id"))
      resolve(blob.signed_id)
    })
  })
}
